import { documentArt } from '@idc-forms/client-app-domain/documents/document-art'
import {
  type DocumentCardSource,
  DocumentCardType,
  documentCardView
} from '@idc-forms/client-app-domain/documents/document-card-view'
import { documentKind } from '@idc-forms/client-app-domain/documents/document-kind'
import {
  backPageLayout,
  frontPageHeight
} from '@idc-forms/client-app-domain/documents/document-stack-layout'
import { FolderLook } from '@idc-forms/client-app-domain/documents/folder-look'
import { forwardRef } from 'react'
import { cn } from '@/shared/lib/utils'
import { CapDocumentArt } from './cap-document-art'
import { CapImage } from './cap-image'
import { CapText } from './cap-text'

/**
 * La tarjeta de un documento en la grilla. Réplica de `features/documentos/ui/document-card.tsx`.
 *
 * ## Qué se replica y qué no
 *
 * Se replican la composición, las medidas y el contenido: la pila de hojas rotadas, el cuadro con
 * borde de las imágenes, el nombre en negrita a una línea y el «Documento · 3 páginas» debajo.
 *
 * **No** se replican los gestos: mantener presionado para compartir, el háptico y el crecimiento de
 * la tarjeta. E5 del contexto común lo dice — fidelidad de contenido y composición, no de chrome —
 * y además compartir desde acá compartiría el documento del CLIENTE desde la sesión de un asesor.
 *
 * ## La geometría de la pila NO se calcula acá
 *
 * `backPageLayout` vive en el paquete de dominio y está probado. La trigonometría no es adorno: al
 * rotar un rectángulo su caja envolvente crece, así que una hoja a alto completo rotada 16° se sale de la
 * tarjeta. Que la web y la app pidan el mismo ángulo al mismo módulo es lo que garantiza que el
 * abanico se vea igual en las dos.
 *
 * ## La hoja NO se recorta
 *
 * La caja de cada página adopta el aspecto REAL de la miniatura (`view.aspect`, del contrato) y la
 * imagen entra con `fit="contain"` sobre el blanco del papel. Antes la caja era carta para todo y
 * la imagen la llenaba con `cover`: un A4 perdía los costados y un plano apaisado salía
 * irreconocible. **La FOTO sigue con `cover` sobre un cuadro**, como en la app.
 */
export type CapDocumentCardProps = Omit<React.ComponentPropsWithoutRef<'button'>, 'onSelect'> & {
  document: DocumentCardSource & { id: string; name: string }
  /** La pinta de la carpeta que lo contiene: decide el arte cuando no hay miniaturas. */
  look?: FolderLook
  onSelect?: () => void
}

export const CapDocumentCard = forwardRef<HTMLButtonElement, CapDocumentCardProps>(
  function CapDocumentCard(
    { document, look = FolderLook.generic, onSelect, className, ...props },
    ref
  ) {
    const view = documentCardView(document)
    const art = documentArt(documentKind(document.mimeType), look)
    const hasThumbnails = view.images.length > 0

    return (
      <button
        ref={ref}
        type="button"
        onClick={onSelect}
        aria-label={`${document.name}. ${view.description}`}
        className={cn('flex w-full flex-col items-stretch gap-1 text-left', className)}
        {...props}
      >
        <div className="flex aspect-square w-full items-center justify-start">
          {view.type === DocumentCardType.image ? (
            <div className="cap-border-neutral-200 h-full w-full overflow-hidden rounded-[var(--cap-radius-lg)] border">
              {hasThumbnails ? (
                <CapImage src={view.images[0]} alt="" className="h-full w-full" />
              ) : (
                <CapDocumentArt art={art} />
              )}
            </div>
          ) : (
            <div className="relative flex h-full w-full items-center justify-start">
              {hasThumbnails ? (
                view.images.map((source, index) => (
                  <div
                    key={source}
                    className="cap-border-neutral-200 absolute bottom-0 overflow-hidden rounded-[var(--cap-radius-md)] border bg-white"
                    style={{
                      aspectRatio: view.aspect,
                      zIndex: view.images.length - index,
                      ...(index === 0
                        ? { left: 0, height: frontPageHeight(view.aspect) }
                        : toCssLayout(backPageLayout(index, view.aspect)))
                    }}
                  >
                    <CapImage src={source} alt="" fit="contain" className="h-full w-full" />
                  </div>
                ))
              ) : (
                <div
                  className="cap-border-neutral-200 absolute bottom-0 left-0 overflow-hidden rounded-[var(--cap-radius-md)] border"
                  style={{ aspectRatio: view.aspect, height: frontPageHeight(view.aspect) }}
                >
                  <CapDocumentArt art={art} />
                </div>
              )}
            </div>
          )}
        </div>

        <CapText variant="body" weight="bold" truncate>
          {document.name}
        </CapText>
        <CapText variant="caption" tone="subtle" truncate>
          {view.description}
        </CapText>
      </button>
    )
  }
)

/**
 * El layout del dominio habla en la forma de React Native (`transform` como lista de operaciones).
 * Traducirlo a CSS es esta función y nada más: los NÚMEROS —el ángulo, el alto, los dos anclajes—
 * salen tal cual del paquete, que es el punto de que la geometría viva ahí.
 */
function toCssLayout(layout: ReturnType<typeof backPageLayout>): React.CSSProperties {
  return {
    height: layout.height,
    right: layout.right,
    bottom: layout.bottom,
    transformOrigin: layout.transformOrigin,
    transform: `rotate(${layout.transform[0].rotate})`
  }
}
